import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, PageTitle, CommonCss, Card } from 'styles/common.styles';
import { theme } from 'styles/theme';
import { SEARCH_LABEL_W } from 'styles/theme';
import {
  Meta,
  ModeTabBar,
  ModeTab,
} from 'components/system/SystemPageChrome.styles';

import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import { showAlert } from 'utils/toastBridge';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import { loadDepts, getCachedDepts } from 'master/deptStore';
import {
  ACCOUNT_STATUS_OPTIONS,
  loadUsers,
  getCachedUsers,
  filterUsers,
  enrichUserRows,
} from 'master/userAccountStore';
import {
  PERM_PACKAGE_OPTIONS,
  SCOPE_TYPE_OPTIONS,
  APPROVAL_BIZ_OPTIONS,
  APPROVAL_STAGE_OPTIONS,
  YN_OPTIONS,
  STANDARD_PERM_PACKAGES,
  PERM_ACTION_LABELS,
  createPermUid,
  loadUserPerms,
  saveUserPerms,
  loadUserApprovals,
  saveUserApprovals,
  validateUserPerms,
  validateUserApprovals,
  joinDeptNames,
  buildPermPreview,
} from 'master/permissionStore';

const USER_GRID_ID = 'perm-user';
const PERM_GRID_ID = 'perm-package';
const APPR_GRID_ID = 'perm-approval';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const Panel = styled(Card)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0;
  overflow: hidden;
  flex: ${(p) => p.$flex || 1};
`;

const BottomRow = styled.div`
  display: flex;
  gap: 8px;
  flex: ${(p) => p.$flex || 1};
  min-height: 0;
`;

const GridBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;


const PreviewPanel = styled(Card)`
  flex-shrink: 0;
  max-height: 132px;
  overflow: auto;
  padding: 8px 12px;
`;

const PreviewTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin-bottom: 4px;
`;

const PreviewLine = styled.div`
  font-size: 0.78rem;
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
`;

/**
 * 기준정보 — 사용자 권한설정
 *
 * 합의 구조 (업무그룹 폐기):
 * - 상단: 사용자 (조회 전용 — 계정은 사용자관리에서)
 * - 좌하: 업무권한 배정 = 표준 패키지 × 적용범위 (희소 배정)
 * - 우하: 승인책임 = 승인/확정/마감 책임자 지정 (일반 권한과 분리)
 * - 하단: 최종 권한 미리보기 — "왜 이 권한이 있는가"를 문장으로 표시
 */
const UserPermManageInner = () => {
  const { user, isMaster } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const userGridRef = useRef(null);
  const permGridRef = useRef(null);
  const apprGridRef = useRef(null);
  const selectedUidRef = useRef('');
  const allPermsRef = useRef([]);
  const allApprovalsRef = useRef([]);
  const [selectedHint, setSelectedHint] = useState('사용자를 선택하면 업무권한·승인책임을 설정할 수 있습니다.');
  const [previewLines, setPreviewLines] = useState([]);
  // 대상부서 팝업 — grid: 'perm' | 'appr', target: 행 uid
  const [deptLookup, setDeptLookup] = useState({ open: false, grid: null });
  const lookupTargetRef = useRef('');

  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } =
    useActiveGridCrudActions({
      guardAdd: (id) => {
        if (id === USER_GRID_ID) return '계정 등록은 사용자관리 화면에서 하세요.';
        if (!selectedUidRef.current) return '사용자를 먼저 선택하세요.';
        return null;
      },
      guardDelete: (id) => {
        if (id === USER_GRID_ID) return '계정 삭제는 사용자관리 화면에서 하세요.';
        if (!selectedUidRef.current) return '사용자를 먼저 선택하세요.';
        return null;
      },
    });

  const searchDefaults = useMemo(
    () => ({ tenantCode, loginId: '', userNm: '', status: 'ACTIVE' }),
    [tenantCode]
  );
  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue } = methods;

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [tenantCode, setValue]);

  const [deptRows, setDeptRows] = useState(() =>
    (getCachedDepts(tenantCode) || []).filter((r) => r.useYn !== 'N')
  );
  useEffect(() => {
    let cancelled = false;
    loadDepts(tenantCode)
      .then((rows) => {
        if (!cancelled) setDeptRows((rows || []).filter((r) => r.useYn !== 'N'));
      })
      .catch(() => {
        if (!cancelled) setDeptRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantCode]);

  /** 선택 사용자 행 (그리드 기준) */
  const getSelectedUserRow = useCallback(() => {
    const rows = userGridRef.current?.getData?.() || [];
    return rows.find((r) => r.accountUid === selectedUidRef.current) || null;
  }, []);

  /** 미리보기 갱신 — 현재 편집 중인 그리드 상태 기준 */
  const refreshPreview = useCallback(() => {
    const userRow = getSelectedUserRow();
    if (!userRow) {
      setPreviewLines([]);
      return;
    }
    const perms = permGridRef.current?.getData?.() || [];
    const approvals = apprGridRef.current?.getData?.() || [];
    setPreviewLines(buildPermPreview(userRow, perms, approvals));
  }, [getSelectedUserRow]);

  const userColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '사용자ID', field: 'loginId', size: 110 }),
      hiCol.roTextColumn({ header: '사용자명', field: 'userNm', size: 100 }),
      hiCol.roTextColumn({ header: '주관부서', field: 'deptName', size: 130 }),
      hiCol.roTextColumn({ header: '계정유형', field: 'accountTypeNm', size: 100, align: 'center' }),
      hiCol.roTextColumn({ header: '계정상태', field: 'statusNm', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '비고', field: 'remark', size: 200 }),
    ],
    []
  );

  const openDeptLookup = useCallback((grid, row) => {
    const uid = grid === 'perm' ? row?.permUid : row?.apprUid;
    if (!uid) return;
    if (row?.scopeType !== 'PICK') {
      showAlert('적용범위를 「선택부서」로 지정한 행에서만 대상부서를 선택합니다.', 'warning');
      return;
    }
    lookupTargetRef.current = uid;
    setDeptLookup({ open: true, grid });
  }, []);

  const permColumns = useMemo(
    () => [
      hiCol.edComboColumn({
        header: '업무권한',
        field: 'packageCode',
        size: 150,
        options: PERM_PACKAGE_OPTIONS,
        required: true,
        title: '시스템 표준 업무권한 패키지 (메뉴+일반 Action 묶음)',
      }),
      hiCol.edComboColumn({
        header: '적용범위',
        field: 'scopeType',
        size: 130,
        options: SCOPE_TYPE_OPTIONS,
        required: true,
      }),
      hiCol.edTextColumn({
        header: '대상부서',
        field: 'deptNamesDisp',
        size: 190,
        editable: false,
        showSearch: true,
        searchTitle: '대상부서 선택 (선택 시 추가 · 이미 있으면 제거)',
        title: '적용범위가 「선택부서」일 때만 지정',
        onSearch: ({ row }) => openDeptLookup('perm', row),
      }),
      hiCol.edComboColumn({ header: '사용', field: 'useYn', size: 70, options: YN_OPTIONS }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 160 }),
    ],
    [openDeptLookup]
  );

  const apprColumns = useMemo(
    () => [
      hiCol.edComboColumn({
        header: '책임업무',
        field: 'bizArea',
        size: 110,
        options: APPROVAL_BIZ_OPTIONS,
        required: true,
      }),
      hiCol.edComboColumn({
        header: '책임단계',
        field: 'stage',
        size: 100,
        options: APPROVAL_STAGE_OPTIONS,
        required: true,
        title: '승인 / 확정 / 마감 — 고위험 Action은 여기서만 부여',
      }),
      hiCol.edComboColumn({
        header: '적용범위',
        field: 'scopeType',
        size: 130,
        options: SCOPE_TYPE_OPTIONS,
        required: true,
      }),
      hiCol.edTextColumn({
        header: '대상부서',
        field: 'deptNamesDisp',
        size: 170,
        editable: false,
        showSearch: true,
        searchTitle: '대상부서 선택 (선택 시 추가 · 이미 있으면 제거)',
        onSearch: ({ row }) => openDeptLookup('appr', row),
      }),
      hiCol.edComboColumn({ header: '사용', field: 'useYn', size: 70, options: YN_OPTIONS }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 150 }),
    ],
    [openDeptLookup]
  );

  const userGrid = useHiGridHook({
    gridRef: userGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
    defaultRow: {},
  });

  const permGrid = useHiGridHook({
    gridRef: permGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: {
      permUid: '',
      accountUid: '',
      packageCode: '',
      scopeType: 'OWN',
      deptUids: [],
      deptCodes: [],
      deptNames: [],
      deptNamesDisp: '',
      useYn: 'Y',
      remark: '',
    },
  });

  const apprGrid = useHiGridHook({
    gridRef: apprGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: {
      apprUid: '',
      accountUid: '',
      bizArea: 'GEN',
      stage: 'APPROVE',
      scopeType: 'OWN',
      deptUids: [],
      deptCodes: [],
      deptNames: [],
      deptNamesDisp: '',
      useYn: 'Y',
      remark: '',
    },
  });

  const withDisp = useCallback(
    (row) => ({ ...row, deptNamesDisp: joinDeptNames(row.deptNames) }),
    []
  );

  /** 현재 선택 사용자의 편집분을 전체 캐시에 합친다. */
  const mergeCurrentRows = useCallback(() => {
    const uid = selectedUidRef.current;
    if (!uid) return;
    const perms = permGridRef.current?.getData?.() || [];
    allPermsRef.current = [
      ...allPermsRef.current.filter((r) => r.accountUid !== uid),
      ...perms.map((r) => ({ ...r, accountUid: uid })),
    ];
    const approvals = apprGridRef.current?.getData?.() || [];
    allApprovalsRef.current = [
      ...allApprovalsRef.current.filter((r) => r.accountUid !== uid),
      ...approvals.map((r) => ({ ...r, accountUid: uid })),
    ];
  }, []);

  const selectUser = useCallback(
    (row) => {
      if (!row?.accountUid) return;
      if (selectedUidRef.current && selectedUidRef.current !== row.accountUid) {
        mergeCurrentRows();
      }
      selectedUidRef.current = row.accountUid;
      setSelectedHint(
        `${row.loginId || ''} · ${row.userNm || ''}${row.deptName ? ` · 주관 ${row.deptName}` : ''}`
      );
      permGridRef.current?.setData?.(
        allPermsRef.current.filter((r) => r.accountUid === row.accountUid).map(withDisp)
      );
      apprGridRef.current?.setData?.(
        allApprovalsRef.current.filter((r) => r.accountUid === row.accountUid).map(withDisp)
      );
      // setData 반영 후 미리보기
      setTimeout(refreshPreview, 0);
    },
    [mergeCurrentRows, withDisp, refreshPreview]
  );

  const handleSearch = useCallback(async () => {
    const users = enrichUserRows(await loadUsers(tenantCode, user));
    allPermsRef.current = loadUserPerms(tenantCode);
    allApprovalsRef.current = loadUserApprovals(tenantCode);
    const filtered = filterUsers(users, getValues());
    userGridRef.current?.setData?.(filtered);

    const still = filtered.find((r) => r.accountUid === selectedUidRef.current);
    if (still) {
      selectedUidRef.current = '';
      selectUser(still);
      return;
    }
    selectedUidRef.current = '';
    setSelectedHint('사용자를 선택하면 업무권한·승인책임을 설정할 수 있습니다.');
    permGridRef.current?.setData?.([]);
    apprGridRef.current?.setData?.([]);
    setPreviewLines([]);
  }, [tenantCode, user, getValues, selectUser]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 최초 조회
  }, []);

  useEffect(() => {
    userGrid.onSelectRow = (row) => selectUser(row);
    userGrid.onRowClicked = ({ data }) => selectUser(data);
    permGrid.onAfterSaveCell = () => refreshPreview();
    apprGrid.onAfterSaveCell = () => refreshPreview();
  }, [userGrid, permGrid, apprGrid, selectUser, refreshPreview]);

  const handlePermAdd = useCallback(async () => {
    const accountUid = selectedUidRef.current;
    if (!accountUid) {
      showAlert('사용자를 먼저 선택하세요.', 'warning');
      return;
    }
    const used = new Set(
      (permGridRef.current?.getData?.() || [])
        .filter((r) => r.mod !== 'D')
        .map((r) => r.packageCode)
    );
    const firstPkg = PERM_PACKAGE_OPTIONS.find((o) => !used.has(o.value));
    await permGrid.handleAdd({
      permUid: createPermUid('UP'),
      accountUid,
      packageCode: firstPkg?.value || '',
      scopeType: 'OWN',
      deptUids: [],
      deptCodes: [],
      deptNames: [],
      deptNamesDisp: '',
      useYn: 'Y',
      remark: '',
    });
    refreshPreview();
  }, [permGrid, refreshPreview]);

  const handlePermDelete = useCallback(async () => {
    await permGrid.handleDelete();
    refreshPreview();
  }, [permGrid, refreshPreview]);

  const handleApprAdd = useCallback(async () => {
    const accountUid = selectedUidRef.current;
    if (!accountUid) {
      showAlert('사용자를 먼저 선택하세요.', 'warning');
      return;
    }
    await apprGrid.handleAdd({
      apprUid: createPermUid('UA'),
      accountUid,
      bizArea: 'GEN',
      stage: 'APPROVE',
      scopeType: 'OWN',
      deptUids: [],
      deptCodes: [],
      deptNames: [],
      deptNamesDisp: '',
      useYn: 'Y',
      remark: '',
    });
    refreshPreview();
  }, [apprGrid, refreshPreview]);

  const handleApprDelete = useCallback(async () => {
    await apprGrid.handleDelete();
    refreshPreview();
  }, [apprGrid, refreshPreview]);

  /** 팝업에서 부서 선택 — 이미 포함이면 제거, 없으면 추가 (토글) */
  const applyDeptLookup = useCallback(
    (dept) => {
      const target = lookupTargetRef.current;
      if (!dept?.deptUid || !target) return;
      const gridRef = deptLookup.grid === 'perm' ? permGridRef : apprGridRef;
      const uidField = deptLookup.grid === 'perm' ? 'permUid' : 'apprUid';
      const rows = gridRef.current?.getData?.() || [];
      const idx = rows.findIndex((r) => r[uidField] === target);
      if (idx < 0) return;
      const row = rows[idx];
      const uids = [...(row.deptUids || [])];
      const codes = [...(row.deptCodes || [])];
      const names = [...(row.deptNames || [])];
      const pos = uids.indexOf(dept.deptUid);
      if (pos >= 0) {
        uids.splice(pos, 1);
        codes.splice(pos, 1);
        names.splice(pos, 1);
      } else {
        uids.push(dept.deptUid);
        codes.push(dept.deptCode || '');
        names.push(dept.deptName || '');
      }
      gridRef.current?.setCellValues?.(idx, {
        deptUids: uids,
        deptCodes: codes,
        deptNames: names,
        deptNamesDisp: joinDeptNames(names),
      });
      setTimeout(refreshPreview, 0);
    },
    [deptLookup.grid, refreshPreview]
  );

  const deptLookupColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 110 }),
      hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 160 }),
      hiCol.roTextColumn({ header: '경로', field: 'pathCode', size: 200 }),
    ],
    []
  );
  const deptLookupRows = useMemo(
    () => deptRows.map((d) => ({ ...d, id: d.deptUid })),
    [deptRows]
  );

  const handleSave = useCallback(() => {
    mergeCurrentRows();
    const users = getCachedUsers(tenantCode);
    const perms = allPermsRef.current.filter((r) => r.mod !== 'D');
    const approvals = allApprovalsRef.current.filter((r) => r.mod !== 'D');

    const permValidation = validateUserPerms(perms, users);
    if (!permValidation.ok) {
      showAlert(permValidation.message, 'warning');
      return;
    }
    const apprValidation = validateUserApprovals(approvals, users);
    if (!apprValidation.ok) {
      showAlert(apprValidation.message, 'warning');
      return;
    }

    allPermsRef.current = saveUserPerms(tenantCode, perms);
    allApprovalsRef.current = saveUserApprovals(tenantCode, approvals);
    if (selectedUidRef.current) {
      permGridRef.current?.setData?.(
        allPermsRef.current
          .filter((r) => r.accountUid === selectedUidRef.current)
          .map(withDisp)
      );
      apprGridRef.current?.setData?.(
        allApprovalsRef.current
          .filter((r) => r.accountUid === selectedUidRef.current)
          .map(withDisp)
      );
    }
    setTimeout(refreshPreview, 0);
    showAlert(
      '저장했습니다.\n(BE 연동 전 PoC — 메뉴 필터·서버 판정 적용은 후속)',
      'success'
    );
  }, [tenantCode, user, mergeCurrentRows, withDisp, refreshPreview]);

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'loginId',
          label: '사용자ID',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'userNm',
          label: '사용자명',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'status',
          label: '계정상태',
          type: 'select',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_W,
          options: [{ value: '', label: '전체' }, ...ACCOUNT_STATUS_OPTIONS],
        },
      ],
    ],
    []
  );

  const packageHint = useMemo(
    () =>
      STANDARD_PERM_PACKAGES.map(
        (p) => `${p.name}: ${p.actions.map((a) => PERM_ACTION_LABELS[a] || a).join('·')}`
      ).join('  |  '),
    []
  );

  const getPermExportData = useCallback(
    () => permGridRef.current?.getFilteredData?.() ?? permGridRef.current?.getData?.() ?? [],
    []
  );
  const getPermExportCols = useCallback(() => permGridRef.current?.getDataColumns?.() ?? [], []);
  const getApprExportData = useCallback(
    () => apprGridRef.current?.getFilteredData?.() ?? apprGridRef.current?.getData?.() ?? [],
    []
  );
  const getApprExportCols = useCallback(() => apprGridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: PERM_GRID_ID,
    label: '업무권한',
    getData: getPermExportData,
    getDataColumns: getPermExportCols,
    joinKeys: ['permUid'],
    order: 0,
  });
  useRegisterGridExport({
    id: APPR_GRID_ID,
    label: '승인책임',
    getData: getApprExportData,
    getDataColumns: getApprExportCols,
    joinKeys: ['apprUid'],
    order: 1,
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: '사용자권한설정',
  });

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      {
        id: 'add',
        label: '추가',
        icon: '➕',
        onClick: handleActiveAdd,
        hotkey: 'F8',
        title: activeLabel ? `추가 → ${activeLabel} (F8)` : '추가 (F8)',
      },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
        title: activeLabel ? `삭제 → ${activeLabel} (F9)` : '삭제 (F9)',
      },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSave,
        hotkey: 'F10',
        variant: 'success',
      },
      { id: 'excel', label: '엑셀', icon: '📥', onClick: openExcelDialog, hotkey: 'F11' },
    ],
    [handleSearch, handleActiveAdd, handleActiveDelete, handleSave, openExcelDialog, activeLabel]
  );

  if (!isMaster) {
    return (
      <PageContainer>
        <PageTitle>사용자 권한설정</PageTitle>
        <Meta>기관 관리자 전용 화면입니다. 접근 권한이 없습니다.</Meta>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} />
        <CollapsibleSearchPanel
          storageKey="master-user-perm"
          prefsUserId={prefsUserId}
          summary="사용자 권한설정"
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Meta>
        활성 그리드: <strong style={{ color: theme.colors.primary }}>{activeLabel || '—'}</strong>
        {' · '}Alt+F2 순환{activeGridId ? ` · F8/F9 → ${activeLabel}` : ''}
        {' · '}기관 {tenantCode}
      </Meta>

      <Stack>
        <Panel $flex={1}>
          <ActiveGridHeader gridId={USER_GRID_ID} extra="계정 등록·수정은 사용자관리에서">
            사용자
          </ActiveGridHeader>
          <GridBody>
            <ActiveGridScope
              gridId={USER_GRID_ID}
              label="사용자"
              order={0}
              canAdd={false}
              canDelete={false}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <HiGridComponent
                ref={userGridRef}
                columns={userColumns}
                height="100%"
                showRowNumber
                rowNumberSize={56}
                headerActive={activeGridId === USER_GRID_ID}
                {...userGrid.contextMenuProps}
                enableHeaderMenu
                enableColumnFilters
                enableFreeze
                gridId={USER_GRID_ID}
                prefsUserId={prefsUserId}
              />
            </ActiveGridScope>
          </GridBody>
        </Panel>

        <BottomRow $flex={1.15}>
          <Panel $flex={1.15}>
            <ActiveGridHeader gridId={PERM_GRID_ID} extra="표준 패키지 × 적용범위 · 필요한 행만 배정">
              업무권한
            </ActiveGridHeader>
            <Meta style={{ padding: '4px 12px' }} title={packageHint}>
              {selectedHint}
            </Meta>
            <GridBody>
              <ActiveGridScope
                gridId={PERM_GRID_ID}
                label="업무권한"
                order={1}
                onAdd={handlePermAdd}
                onDelete={handlePermDelete}
                style={{ flex: 1, minHeight: 0, height: '100%' }}
              >
                <GridExportFocusScope gridId={PERM_GRID_ID} style={{ flex: 1, minHeight: 0, height: '100%' }}>
                  <HiGridComponent
                    ref={permGridRef}
                    columns={permColumns}
                    height="100%"
                    showRowNumber
                    rowNumberSize={56}
                    showModColumn
                    headerActive={activeGridId === PERM_GRID_ID}
                    {...permGrid.contextMenuProps}
                    enableHeaderMenu
                    enableColumnFilters
                    enableFreeze
                    enableQuickExport
                    exportFileName="업무권한"
                    gridId={PERM_GRID_ID}
                    prefsUserId={prefsUserId}
                    colOptions={permGrid.colOptions}
                  />
                </GridExportFocusScope>
              </ActiveGridScope>
            </GridBody>
          </Panel>

          <Panel $flex={1}>
            <ActiveGridHeader gridId={APPR_GRID_ID} extra="승인·확정·마감 — 일반 권한과 분리">
              승인책임
            </ActiveGridHeader>
            <Meta style={{ padding: '4px 12px' }}>
              팀장·책임자에게만 부여 · 사유는 비고에 기록
            </Meta>
            <GridBody>
              <ActiveGridScope
                gridId={APPR_GRID_ID}
                label="승인책임"
                order={2}
                onAdd={handleApprAdd}
                onDelete={handleApprDelete}
                style={{ flex: 1, minHeight: 0, height: '100%' }}
              >
                <GridExportFocusScope gridId={APPR_GRID_ID} style={{ flex: 1, minHeight: 0, height: '100%' }}>
                  <HiGridComponent
                    ref={apprGridRef}
                    columns={apprColumns}
                    height="100%"
                    showRowNumber
                    rowNumberSize={56}
                    showModColumn
                    headerActive={activeGridId === APPR_GRID_ID}
                    {...apprGrid.contextMenuProps}
                    enableHeaderMenu
                    enableColumnFilters
                    enableFreeze
                    enableQuickExport
                    exportFileName="승인책임"
                    gridId={APPR_GRID_ID}
                    prefsUserId={prefsUserId}
                    colOptions={apprGrid.colOptions}
                  />
                </GridExportFocusScope>
              </ActiveGridScope>
            </GridBody>
          </Panel>
        </BottomRow>

        <PreviewPanel>
          <PreviewTitle>최종 권한 미리보기</PreviewTitle>
          {previewLines.length ? (
            previewLines.map((line, i) => <PreviewLine key={i}>· {line}</PreviewLine>)
          ) : (
            <PreviewLine>사용자를 선택하면 최종 권한을 문장으로 보여줍니다.</PreviewLine>
          )}
        </PreviewPanel>
      </Stack>

      <CodeLookupDialog
        open={deptLookup.open}
        title="대상부서 선택"
        rows={deptLookupRows}
        columns={deptLookupColumns}
        enableSearch
        searchConfig={{
          codeFields: ['deptCode'],
          nameFields: ['deptName'],
          codeLabel: '부서코드',
          nameLabel: '부서명',
        }}
        onSelect={applyDeptLookup}
        onClose={() => setDeptLookup({ open: false, grid: null })}
      />

      {excelDialog}
    </PageContainer>
  );
};

const UserPermManage = () => (
  <GridExportProvider>
    <ActiveGridProvider>
      <UserPermManageInner />
    </ActiveGridProvider>
  </GridExportProvider>
);

export default UserPermManage;
